import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeviceMaintenance } from '../../../../assets/icons/device-management/maintenance.svg';
import { ReactComponent as MoveBackward } from '../../../../assets/icons/device-management/move-backward.svg';
import { ReactComponent as MoveForward } from '../../../../assets/icons/device-management/move-forward.svg';
// import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { MultiLevelMenuDropdownProperties } from '../../../../interface/shared-component.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchCharingPointWithoutPagination,
  setSelectedDevice,
} from '../../../../redux/slices/DeviceManagementSlice';
// import AutoCompleteSelectField from '../../../shared-components/auto-complete-select-field/AutoCompleteSelectField';
import CustomMultiLevelDropdown from '../../../../shared-components/custom-multi-level-dropdown';
import deviceApi from '../../../../utils/api/device';

/**
 * Next and previous arrow
 * @param {any} data - The data
 * @param {any} deviceId - The deviceId
 * @param {any} direction - The direction
 * @returns {any} The result
 */
function getNextOrPreviousDevice(
  data: MultiLevelMenuDropdownProperties[],
  deviceId: string | undefined,
  direction: 'next' | 'prev' = 'next',
) {
  if (!Array.isArray(data) || !deviceId) return null;

  const flatList: any[] = [];

  /**
   * Function to extract a flat map
   * @param {any} items - The items
   * @param {any} parentGroup - The parentGroup
   * @param {any} parentGroup.id - The id prop
   * @param {any} parentGroup.name - The name prop
   */
  const extractDevices = (items: any[], parentGroup?: { id: string; name: string }) => {
    /* eslint-disable no-restricted-syntax */
    for (const item of items) {
      if (item.subMenu && item.subMenu.length > 0) {
        extractDevices(item.subMenu, parentGroup ?? { id: item.id, name: item.label });
      } else {
        flatList.push({
          ...item,
          groupId: parentGroup?.id,
          groupName: parentGroup?.name,
        });
      }
    }
  };

  extractDevices(data);

  const currentIndex = flatList.findIndex((device) => device.id === deviceId);
  if (currentIndex === -1) return null;

  const targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

  if (targetIndex < 0 || targetIndex >= flatList.length) {
    return null; // Reached start or end
  }

  return flatList[targetIndex];
}

/**
 * Component to display the device search
 * @returns {any} The result
 */
function DeviceSearch() {
  const { t, i18n } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: idInQuery } = useParams<{ id: string }>();
  const { deviceList, selectedChargingPoint, deviceListLoading } = useAppSelector((s) => s.device);

  const [searchParameters] = useSearchParams();
  const searchBy = searchParameters.get('searchBy'); // Gets the 'searchBy' query param

  const [isMaintenanceScheduled, setIsMaintenanceScheduled] = useState<boolean>(false);
  const { type } = useParams<{ type: string }>();
  const { userCustomId } = useParams<{ userCustomId: string }>();

  /**
   * Function to find label of selected id
   * @param {any} items - The items
   * @param {any} id - The id
   * @returns {any} The result
   */
  const findItemById = (
    items: MultiLevelMenuDropdownProperties[],
    id: string,
  ): MultiLevelMenuDropdownProperties | null => {
    let foundItem: MultiLevelMenuDropdownProperties | null = null;

    items.some((item) => {
      if (item.id === id) {
        foundItem = item;
        return true; // stop iteration
      }

      if (item.hasSubMenu && item.subMenu) {
        const subResult = findItemById(item.subMenu, id);
        if (subResult) {
          foundItem = subResult;
          return true; // stop iteration
        }
      }

      return false;
    });

    return foundItem;
  };

  /**
   * Function to filter the station list by name or address
   * @param {string} name - The name
   * @param {string | number} value - The value
   */
  const handleSearchOrSelectDevice = (name: string, value: string | number) => {
    const foundDevice = findItemById(deviceList, value.toString());
    const isDeviceHub = window.location.pathname.includes('ev-device-hub');
    const isExpanded =
      window.location.pathname.includes('charger-expanded') ||
      window.location.pathname.includes('device-expanded');
    const basePathForEndConsumer = isExpanded ? 'charger-expanded' : 'charger';
    let basePathForGlobal = isExpanded ? 'charger-expanded' : 'charger-management';
    if (isDeviceHub) {
      basePathForGlobal = 'ev-device-hub/device-details';
    }

    if (foundDevice?.id && foundDevice?.value) {
      dispatch(
        setSelectedDevice({
          id: foundDevice.id,
          name: foundDevice.label,
          chargingPointSn: foundDevice.value,
        }),
      );

      if (type && userCustomId) {
        navigate({
          pathname: `/ev-admin/end-consumer/${type}/profile/${userCustomId}/${basePathForEndConsumer}/${foundDevice.id}/device-details`,
          search: `?searchBy=${encodeURIComponent(foundDevice.label)}`,
        });
      } else {
        navigate({
          pathname: `/ev-admin/${basePathForGlobal}/${foundDevice.id}/device-details`,
          search: `?searchBy=${encodeURIComponent(foundDevice.label)}`,
        });
      }
    }
  };

  /**
   * Function to next value or previous value
   * @param {'next' | 'prev'} buttonType - The buttonType
   */
  const handleNextOrPrevious = (buttonType: 'next' | 'prev') => {
    const changeData = getNextOrPreviousDevice(deviceList, selectedChargingPoint?.id, buttonType);
    const isDeviceHub = window.location.pathname.includes('ev-device-hub');
    const isExpanded =
      window.location.pathname.includes('charger-expanded') ||
      window.location.pathname.includes('device-expanded');
    const basePathForEndConsumer = isExpanded ? 'charger-expanded' : 'charger';
    let basePathForGlobal = isExpanded ? 'charger-expanded' : 'charger-management';
    if (isDeviceHub) {
      basePathForGlobal = 'ev-device-hub/device-details';
    }

    if (changeData) {
      dispatch(
        setSelectedDevice({
          id: changeData.id,
          name: changeData.label,
          chargingPointSn: changeData.value,
        }),
      );

      if (type && userCustomId) {
        navigate({
          pathname: `/ev-admin/end-consumer/${type}/profile/${userCustomId}/${basePathForEndConsumer}/${changeData.id}/device-details`,
          search: `?searchBy=${encodeURIComponent(changeData.label)}`,
        });
      } else {
        navigate({
          pathname: `/ev-admin/${basePathForGlobal}/${changeData.id}/device-details`,
          search: `?searchBy=${encodeURIComponent(changeData.label)}`,
        });
      }
    }
  };

  /**
   * Func
   */
  const handleMaintenanceAlert = async () => {
    const isDeviceHub = window.location.pathname.includes('ev-device-hub');
    if (isDeviceHub || !selectedChargingPoint?.chargingPointSn) return;
    try {
      const response = await deviceApi.fetchUpcomingMaintenanceSchedule({
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
      });
      setIsMaintenanceScheduled(response?.data?.length > 0);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Function to handle fetch devices based on name
   * @param {any} name - The name
   */
  const handleChangeValueToShow = (name: any) => {
    if (name.length > 1 && name !== selectedChargingPoint?.name) {
      dispatch(fetchCharingPointWithoutPagination({ chargingPointName: name }));
    }
  };

  useEffect(() => {
    // Always fetch the full list on mount so prev/next navigation works even
    // when the user arrives via a direct URL without a searchBy query param.
    dispatch(fetchCharingPointWithoutPagination({ chargingPointName: searchBy || '' }));
  }, []);

  useEffect(() => {
    if (!selectedChargingPoint?.id && idInQuery) {
      handleSearchOrSelectDevice('', idInQuery);
    }
  }, [deviceList]);

  useEffect(() => {
    handleMaintenanceAlert();
  }, [selectedChargingPoint]);

  return (
    <div className="device-search-wrap">
      <div className="device-search-container" dir={i18n.dir()}>
        <span
          role="presentation"
          onClick={() => handleNextOrPrevious('prev')}
          style={{ transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none' }}>
          <MoveBackward />
        </span>
        <CustomMultiLevelDropdown
          inputLabel=""
          valueRequired={false}
          name="chargingPointId"
          placeholder={t('searchHere')}
          selectedValue={selectedChargingPoint?.name || ''}
          onChangeValue={handleSearchOrSelectDevice}
          handleChangeValueToShow={handleChangeValueToShow}
          originalValue={deviceList}
          loading={deviceListLoading}
        />
        <span
          role="presentation"
          onClick={() => handleNextOrPrevious('next')}
          style={{ transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'none' }}>
          <MoveForward />
        </span>
      </div>
      {isMaintenanceScheduled ? (
        <div className="device-maintenance">
          <span role="presentation" title={t('maintenanceAlert')}>
            <DeviceMaintenance />
          </span>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

export default DeviceSearch;
